import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useSignup } from '../contexts/SignupContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import AuthModal from '../components/AuthModal';
import styles from './SubscriptionSuccessPage.module.css';

const VERIFY_CHECKOUT_SESSION_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/verifyCheckoutSession';

function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useTranslation();
  const { clearSignupData } = useSignup();
  const { currentUser } = useAuth();
  const profileIdParam = searchParams.get('profileId');
  const nextInvoiceDateParam = searchParams.get('nextInvoiceDate');
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [profileId, setProfileId] = useState(profileIdParam);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const verifySessionAndFetchProfile = async () => {
      const sessionId = searchParams.get('session_id');
      let finalProfileId = profileId;


      // Si on a un session_id, vérifier la session Stripe pour obtenir le profileId
      if (sessionId) {
        try {
          const response = await fetch(VERIFY_CHECKOUT_SESSION_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
          });

          if (response.ok) {
            const data = await response.json();
            const existingProfileId = data.session?.metadata?.existingProfileId;
            const subscriptionId = data.session?.subscriptionId;


            // Déterminer le profileId à utiliser (priorité : existingProfileId > profileIdParam)
            const profileIdToUpdate = existingProfileId || profileIdParam;

            // Si on a un profileId, mettre à jour le compte en PRO
            if (profileIdToUpdate) {
              finalProfileId = profileIdToUpdate;
              setProfileId(profileIdToUpdate); // Mettre à jour le state pour l'utiliser dans le reste du composant
              try {
                const profileRef = doc(db, 'hairstylists', profileIdToUpdate);
                const profileSnap = await getDoc(profileRef);
                
                if (profileSnap.exists()) {
                  const currentData = profileSnap.data();
                  // Ne mettre à jour que si le compte n'est pas déjà PRO
                  if (currentData.accountType !== 'PRO') {
                    const updateData = {
                      accountType: 'PRO',
                      subscriptionActive: true,
                      subscriptionStartDate: serverTimestamp(),
                      paymentStatus: 'ok',
                      updatedAt: serverTimestamp(),
                    };
                    
                    // Ajouter subscriptionId si disponible
                    if (subscriptionId) {
                      updateData.subscriptionId = subscriptionId;
                    }
                    
                    await updateDoc(profileRef, updateData);
                  } else {
                    const updateData = {
                      subscriptionActive: true,
                      subscriptionStartDate: serverTimestamp(),
                      paymentStatus: 'ok',
                      updatedAt: serverTimestamp(),
                    };
                    
                    if (subscriptionId) {
                      updateData.subscriptionId = subscriptionId;
                    }
                    
                    await updateDoc(profileRef, updateData);
                  }
                } else {
                }
              } catch (updateError) {
                // eslint-disable-next-line no-console
                console.error('[SubscriptionSuccessPage] ❌ Error updating profile to PRO:', updateError);
              }
            } else {
              
              // FALLBACK: Chercher dans la collection users si on a un currentUser
              if (currentUser) {
                try {
                  const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    if (userData.hairstylistProfileId) {
                      finalProfileId = userData.hairstylistProfileId;
                      setProfileId(finalProfileId);
                      
                      // Mettre à jour le profil
                      const profileRef = doc(db, 'hairstylists', finalProfileId);
                      const updateData = {
                        accountType: 'PRO',
                        subscriptionActive: true,
                        subscriptionStartDate: serverTimestamp(),
                        paymentStatus: 'ok',
                        updatedAt: serverTimestamp(),
                      };
                      
                      if (subscriptionId) {
                        updateData.subscriptionId = subscriptionId;
                      }
                      
                      await updateDoc(profileRef, updateData);
                    }
                  }
                } catch (fallbackError) {
                  // eslint-disable-next-line no-console
                  console.error('[SubscriptionSuccessPage] ❌ Error in fallback:', fallbackError);
                }
              }
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[SubscriptionSuccessPage] ❌ Error verifying checkout session:', error);
        }
      } else if (profileIdParam) {
        // Si pas de sessionId mais un profileIdParam, essayer quand même de mettre à jour
        // (cas où l'utilisateur revient directement avec un profileId)
        try {
          const profileRef = doc(db, 'hairstylists', profileIdParam);
          const profileSnap = await getDoc(profileRef);
          
          if (profileSnap.exists()) {
            const currentData = profileSnap.data();
            if (currentData.accountType !== 'PRO') {
              await updateDoc(profileRef, {
                accountType: 'PRO',
                subscriptionActive: true,
                subscriptionStartDate: serverTimestamp(),
                paymentStatus: 'ok',
                updatedAt: serverTimestamp(),
              });
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating profile to PRO (fallback):', error);
        }
      }

      if (!finalProfileId) {
        // Si pas de profileId, rediriger vers la page d'accueil
        navigate('/');
        return;
      }

      // Essayer de récupérer le profil avec plusieurs tentatives (délai de propagation Firestore)
      let attempts = 0;
      const maxAttempts = 5;
      const delay = 500; // 500ms entre chaque tentative

      const tryFetch = async () => {
        try {
          const profileDoc = await getDoc(doc(db, 'hairstylists', finalProfileId));
          if (profileDoc.exists()) {
            setProfileData(profileDoc.data());
            setLoading(false);
            // Nettoyer le Context maintenant que le profil est chargé avec succès
            clearSignupData();
            sessionStorage.removeItem('tempHairstylistProfile');
          } else {
            attempts++;
            if (attempts < maxAttempts) {
              // Réessayer après un délai
              setTimeout(tryFetch, delay);
            } else {
              // Après plusieurs tentatives, si le profil n'est toujours pas trouvé,
              // rediriger vers la page d'accueil (pas vers /signup pour éviter le message)
              navigate('/', { replace: true });
              setLoading(false);
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching profile:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(tryFetch, delay);
          } else {
            navigate('/', { replace: true });
            setLoading(false);
          }
        }
      };

      tryFetch();
    };

    verifySessionAndFetchProfile();
  }, [profileId, searchParams, navigate, clearSignupData]);

  // Scroller en haut quand le contenu est chargé
  useEffect(() => {
    if (!loading && profileData) {
      // Petit délai pour s'assurer que le DOM est complètement rendu
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [loading, profileData]);

  const handleCopyReviewLink = () => {
    const reviewUrl = `${window.location.origin}/hairstylist/${profileId}`;
    navigator.clipboard.writeText(reviewUrl);
    alert(t('payment.linkCopied'));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const localeMap = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE', nl: 'nl-NL' };
    const locale = localeMap[language] || 'fr-FR';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return null;
  }

  const ensureAuthenticatedAndNavigate = (path) => {
    if (currentUser) {
      navigate(path);
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>


        {/* Card Infos du Profil */}
        <section className={styles.profileCard}>
          <h2 className={styles.cardTitle}>{t('payment.subscriptionActivated')}</h2>
          <p className={styles.cardSubtitle}>
            {t('payment.subscriptionActivatedSubtitle')}
          </p>
          <div className={styles.statusBadge}>
            <span className={styles.badgeIcon}>✓</span>
            <span>{t('payment.subscriptionActiveStatus')}</span>
          </div>
        </section>

        {/* Card Détails de l'abonnement */}
        <section className={styles.subscriptionCard}>
          <h2 className={styles.cardTitle}>{t('payment.subscriptionDetails')}</h2>
          <div className={styles.subscriptionDetails}>
            <div className={styles.subscriptionRow}>
              <span className={styles.label}>{t('payment.plan')}</span>
              <span className={styles.value}>{t('payment.professionalPlan')}</span>
            </div>
            <div className={styles.subscriptionRow}>
              <span className={styles.label}>{t('payment.autoRenewal')}</span>
              <span className={styles.value}>{t('payment.enabled')}</span>
            </div>
            <div className={styles.subscriptionRow}>
              <span className={styles.label}>{t('payment.nextInvoice')}</span>
              <span className={styles.value}>
                {nextInvoiceDateParam ? formatDate(nextInvoiceDateParam) : 'N/A'}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={styles.manageSubscriptionButton}
            onClick={() => {
              if (currentUser) {
                // TODO: Rediriger vers Stripe Customer Portal
                alert('Redirection vers Stripe Customer Portal (à implémenter)');
              } else {
                setShowAuthModal(true);
              }
            }}
          >
            {t('payment.manageSubscription')}
          </button>
        </section>

        {/* Section Les Prochaines Étapes */}
        <section className={styles.nextSteps}>
          <h2 className={styles.sectionTitle}>{t('payment.nextSteps')}</h2>
          <div className={styles.stepsGrid}>
            <div className={styles.stepCard}>
              <div className={styles.stepIcon}>✏️</div>
              <h3 className={styles.stepTitle}>{t('payment.completeProfile')}</h3>
              <p className={styles.stepDescription}>
                {t('payment.completeProfileDescription')}
              </p>
              <button
                type="button"
                className={styles.stepButton}
                onClick={() => ensureAuthenticatedAndNavigate(`/hairstylist/${profileId}/edit`)}
              >
                {t('header.editProfile')}
              </button>
            </div>

            <div className={styles.stepCard}>
              <div className={styles.stepIcon}>📸</div>
              <h3 className={styles.stepTitle}>{t('payment.addPhotos')}</h3>
              <p className={styles.stepDescription}>
                {t('payment.addPhotosDescription')}
              </p>
              <button
                type="button"
                className={styles.stepButton}
                onClick={() => ensureAuthenticatedAndNavigate(`/hairstylist/${profileId}/edit`)}
              >
                {t('payment.addPhotosButton')}
              </button>
            </div>

            <div className={styles.stepCard}>
              <div className={styles.stepIcon}>⭐</div>
              <h3 className={styles.stepTitle}>{t('payment.inviteReviews')}</h3>
              <p className={styles.stepDescription}>
                {t('payment.inviteReviewsDescription')}
              </p>
              <button
                type="button"
                className={styles.stepButton}
                onClick={() => {
                  if (currentUser) {
                    handleCopyReviewLink();
                  } else {
                    setShowAuthModal(true);
                  }
                }}
              >
                {t('payment.copyReviewLink')}
              </button>
            </div>
          </div>
        </section>

        {/* Message émotionnel */}
        <section className={styles.emotionalMessage}>
          <p className={styles.messageText}>
            {t('payment.communityMessage')}
          </p>
          <p className={styles.messageText}>
            {t('payment.proudMessage')}
          </p>
          <p className={styles.messageText}>
            {t('payment.ifYouHaveQuestion')} <a href="mailto:cheriecoiffer@gmail.com">cheriecoiffer@gmail.com</a>
          </p>
        </section>

        {/* CTA principal */}
        <section className={styles.mainCTA}>
          <button
            type="button"
            className={styles.ctaButton}
            onClick={() => ensureAuthenticatedAndNavigate(`/hairstylist/${profileId}`)}
          >
            {t('payment.accessStylistSpace')}
          </button>
        </section>

        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

        {/* Footer */}
        <footer className={styles.footer}>
          <div className={styles.footerLinks}>
            <a href="/legal">{t('footer.legal')}</a>
            <span>•</span>
            <a href="/refund">{t('footer.refund')}</a>
            <span>•</span>
            <a href="mailto:cheriecoiffer@gmail.com">{t('footer.contact')}</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default SubscriptionSuccessPage;

